import { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';

interface SJChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function SJChatInput({ onSend, disabled, placeholder }: SJChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <div className="relative flex items-end gap-2 rounded-[14px] border border-[var(--sj-border)] bg-[var(--sj-surface)] p-2 shadow-sm transition-all focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/20">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Describe your business challenge...'}
        disabled={disabled}
        rows={1}
        className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-[var(--sj-text-primary)] placeholder-[var(--sj-text-muted)] outline-none disabled:opacity-50"
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[var(--sj-text-primary)] text-[var(--sj-background)] transition-all hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Send message"
      >
        <Send className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
